// OpenLayers
import VectorImageLayer from "ol/layer/VectorImage.js";
import OlVectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON.js";
import { reprojectFeature } from "utils/OpenLayers/TransformUtils";

// Components
import { OLMapStyles } from "utils/OpenLayers/OLMapStyles";

// Utils
import {
  removeOLLayerByName,
  addPathToMap,
  olAddLayerToGroup,
  getOLLayerByName,
  getOLLayerSource,
  getMainLineStringFromPathLayer
} from "utils/OpenLayers/OLLayerUtils";

/**
 *
 * @param {Object} map
 * @param {Object} path
 * @param {String} color
 * @param {Number} scenarioId
 * @param {Boolean} showPylons
 * @param {Boolean} showLabels
 *
 * @returns {any}
 */
export const olAddPath = (
  map,
  path,
  color,
  scenarioId,
  showPylons,
  showLabels,
  showRoutingWidth,
  availableTechnologies = []
) => {
  const MapStylesObject = new OLMapStyles();

  if (!path?.path || !path?.path?.coordinates) return;

  let pathFeature = new GeoJSON().readFeature(path?.path);
  reprojectFeature(pathFeature);

  let pathStyle = MapStylesObject.genComplexPathStyles(
    path?.metadata,
    color,
    showPylons,
    showLabels,
    showRoutingWidth,
    availableTechnologies
  );

  const pathLayerName = parseInt("".concat(path?.scenario, path?.id));

  // remove layer if exist
  removeOLLayerByName(map, pathLayerName);

  const vectorLayer = new VectorImageLayer({
    name: pathLayerName,
    source: new OlVectorSource(),
    style: pathStyle,
    layerType: "ScenarioPath",
    declutter: true
  });

  vectorLayer.set("gIsPathLayer", true);

  olAddLayerToGroup(map, "optimalPathGroup", vectorLayer);

  let pointsData = [...path.path.coordinates];
  if (pointsData.length < 1) {
    return;
  }

  let viewProjection = map.getView().getProjection();

  addPathToMap(path, vectorLayer, viewProjection, path?.id, scenarioId);
};

export const olRemovePath = (map, pathId, scenarioId) => {
  const pathLayerName = parseInt("".concat(scenarioId, pathId));
  removeOLLayerByName(map, pathLayerName);
};

/**
 * Change the color of a path. Add a Feature property named
 * color that then can be used by the renderers to apply a given
 * color
 * @param {object} map           OpenLayersMap instance
 * @param {string} pathLayerName Name of the path layer to assing
 *                               the property to
 * @param {string} color         New color to apply
 */
export const olSetPathColor = (map, pathLayerName, color) => {
  let pathLayer = getOLLayerByName(map, pathLayerName);

  if (pathLayer) {
    let ls = getMainLineStringFromPathLayer(pathLayer);
    if (ls) {
      ls.set("color", color);
    }
  }
};

/**
 * Retrieves the OpenLayers path layer entity for a given path.
 * Allows checking if the layer exists and is visible.
 *
 * @param {Object} map - The OpenLayers map instance.
 * @param {Object} path - The path object (should have .id and .scenario).
 * @returns {ol/layer/VectorImage|undefined} The path layer if found, otherwise undefined.
 */
export function olGetPath(map, path) {
  if (
    !path ||
    typeof path.id === "undefined" ||
    typeof path.scenario === "undefined"
  ) {
    return undefined;
  }
  // The layer name is generated as parseInt("".concat(path.scenario, path.id))
  const pathLayerName = parseInt("".concat(path.scenario, path.id));
  const layer = getOLLayerByName(map, pathLayerName);

  // Check if the layer is visible
  if (layer && typeof layer.getVisible === "function") {
    return layer;
  }
  return undefined;
}

export function toggleItem2D(itemName, collection, isShown) {
  if (!collection || !itemName) return;

  const items = new Map();

  items.set("pylons", "showNodes");
  items.set("labels", "showLabels");
  items.set("routingWidth", "showRoutingWidth");

  const layers = collection.getArray();

  for (const pathLayer of layers) {
    const source = getOLLayerSource(pathLayer);
    const features = source.getFeatures();
    const linestring = features.find(
      item =>
        item.getGeometry().getType() === "LineString" && item.get("gIsPath")
    );

    if (linestring) {
      linestring.set(items.get(itemName), isShown);
    }
  }
}
